/**
 * OnboardingWizard — Geführter Ersteinstieg für neue Accounts
 *
 * Full-Screen-Overlay mit 4 Schritten (kundenzentriert, so kurz wie möglich):
 *   1. Willkommen & Value-Pitch
 *   2. Über dich & deine Marke (Unternehmen, Angebot, Zielgruppe, Themen)
 *   3. Tonalität, Ziele, Intensität & Kanäle
 *   4. Ersten Monatsplan mit 1 Klick erzeugen → Kalender
 *
 * Das in Schritt 2+3 erfasste Markenprofil wird im Account gespeichert
 * (PATCH /account/brand-profile) und speist danach den 1-Klick-Monatsplan.
 *
 * Props:
 *   onComplete  — Callback wenn Onboarding fertig (Wizard ausblenden)
 *   onSkip      — Callback wenn Nutzer Wizard überspringt
 */

import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { trackEvent } from "../lib/trackEvent";
import { getVariant } from "../lib/ab_utils";
import { shouldConfirmChannellessExit } from "./onboardingCompletionModel";
import {
  Sparkles, ChevronRight, ChevronLeft, Loader2, CheckCircle,
  X, Zap, Eye, Plus,
  AlertTriangle, ArrowRight, Wifi
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Monats- & Datums-Hilfsfunktionen ──────────────────────────────────────────
const MONTH_NAMES = [
  "", "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

const now = new Date();
const CURRENT_MONTH = now.getMonth() + 1;
const CURRENT_YEAR  = now.getFullYear();

// ── Konfiguration — identisch mit AutopilotWizard ──────────────────────────────
const INTENSITIES = [
  { id: "leicht",   label: "Entspannt",   sub: "~2 Posts/Woche",  emoji: "🌿" },
  { id: "normal",   label: "Regelmäßig",  sub: "~3 Posts/Woche",  emoji: "🔥" },
  { id: "intensiv", label: "Aktiv",       sub: "~5 Posts/Woche",  emoji: "⚡" },
];

const GOALS = [
  { id: "Sichtbarkeit", emoji: "👁️",  label: "Sichtbarkeit" },
  { id: "Vertrauen",    emoji: "🤝",  label: "Vertrauen aufbauen" },
  { id: "Buchungen",    emoji: "📅",  label: "Mehr Buchungen" },
  { id: "Community",    emoji: "💬",  label: "Community stärken" },
  { id: "Events",       emoji: "🎯",  label: "Events & Aktionen" },
];

// Bluesky zuerst: der Kanal, den Trial-Nutzer sofort verbinden können (Kanal-Gate:
// Testphase = nur Bluesky; alle weiteren Kanäle sind Teil der gekauften Pakete).
const PLATFORMS = [
  { id: "bluesky",         label: "Bluesky",          emoji: "🦋" },
  { id: "instagram",       label: "Instagram",       emoji: "📸" },
  { id: "facebook",        label: "Facebook",         emoji: "👍" },
  { id: "linkedin",        label: "LinkedIn",         emoji: "💼" },
  { id: "tiktok",          label: "TikTok",           emoji: "🎵" },
  { id: "google_business", label: "Google Business",  emoji: "📍" },
];

const TONES = [
  { id: "warm",          label: "Warm & nahbar" },
  { id: "professionell", label: "Professionell" },
  { id: "locker",        label: "Locker & frech" },
  { id: "inspirierend",  label: "Inspirierend" },
  { id: "sachlich",      label: "Sachlich" },
];

const TOPIC_SUGGESTIONS = [
  "Angebote", "Tipps & Wissen", "Hinter den Kulissen",
  "Kundenstimmen", "Erfolgsgeschichten", "Team", "Aktionen", "FAQ",
];

// ── Fortschritts-Balken ────────────────────────────────────────────────────────
function StepBar({ step, total }) {
  return (
    <div className="flex items-center gap-1.5 px-6 py-4">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={`h-1 flex-1 rounded-full transition-all duration-500 ${
            i < step ? "bg-fox-fire" : i === step ? "bg-orange-300" : "bg-stone-200"
          }`}
        />
      ))}
      <span className="text-xs text-stone-400 ml-2 flex-shrink-0">
        {step + 1} / {total}
      </span>
    </div>
  );
}

// ── Schritt 1: Willkommen ──────────────────────────────────────────────────────
function StepWelcome({ onNext, onSkip, ctaLabel = "Jetzt in 2 Minuten einrichten" }) {
  return (
    <div className="flex flex-col items-center text-center px-8 py-10 space-y-8 max-w-lg mx-auto">
      {/* Fox + Headline */}
      <div className="space-y-3">
        <div className="w-24 h-24 mx-auto bg-fox-tint rounded-3xl flex items-center justify-center shadow-lg shadow-orange-100 ring-1 ring-fox-fire/15">
          <img src="/images/logo.png" alt="Förderfuchs" width="240" height="276" className="h-16 w-auto" />
        </div>
        <h1 className="font-heading text-3xl font-bold text-stone-900 leading-tight">
          Dein erster Monatsplan<br />
          <span className="text-fox-fire">in 2 Minuten</span>
        </h1>
        <p className="text-stone-500 text-base leading-relaxed">
          Beantworte ein paar kurze Fragen — danach erstellt Förderfuchs deinen
          kompletten Monatsplan mit einem Klick.
        </p>
      </div>

      {/* Value Props */}
      <div className="w-full space-y-2.5">
        {[
          { icon: Sparkles, color: "text-orange-500 bg-orange-50", title: "Plan auf Knopfdruck", desc: "Ein ganzer Monat Content — automatisch verteilt." },
          { icon: Eye,      color: "text-violet-500 bg-violet-50", title: "Auf deine Marke zugeschnitten",  desc: "Themen, Tonalität und Ziele fließen automatisch ein." },
          { icon: Zap,      color: "text-sky-500 bg-sky-50",       title: "Du behältst die Kontrolle", desc: "Jeden Beitrag vor der Veröffentlichung prüfen & anpassen." },
        ].map(({ icon: Icon, color, title, desc }) => (
          <div key={title} className="flex items-start gap-3 bg-stone-50 rounded-xl px-4 py-3 text-left">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${color}`}>
              <Icon size={15} />
            </div>
            <div>
              <p className="text-sm font-semibold text-stone-800">{title}</p>
              <p className="text-xs text-stone-500 leading-snug">{desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* CTAs */}
      <div className="w-full space-y-2">
        <button
          onClick={onNext}
          className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-fox-fire text-white rounded-xl font-semibold text-base hover:bg-orange-600 transition-colors shadow-sm shadow-orange-200"
        >
          {ctaLabel}
          <ArrowRight size={18} />
        </button>
        <button
          onClick={onSkip}
          className="w-full text-sm text-stone-400 hover:text-stone-600 transition-colors py-2"
        >
          Direkt zur App →
        </button>
      </div>
    </div>
  );
}

// ── Schritt 2: Über dich & deine Marke ────────────────────────────────────────
function StepBrand({ brand, setBrand, onNext, onBack }) {
  const [topicInput, setTopicInput] = useState("");

  const update = (key, value) => setBrand(prev => ({ ...prev, [key]: value }));

  const addTopic = (raw) => {
    const t = (raw ?? topicInput).trim();
    if (!t) return;
    setBrand(prev =>
      prev.topics.includes(t) ? prev : { ...prev, topics: [...prev.topics, t] }
    );
    setTopicInput("");
  };
  const removeTopic = (t) =>
    setBrand(prev => ({ ...prev, topics: prev.topics.filter(x => x !== t) }));

  // Angebot ODER mind. 1 Thema reicht zum Weitermachen — niemals einsperren.
  const canProceed = brand.offer.trim().length > 0 || brand.topics.length > 0;

  return (
    <div className="px-6 py-6 space-y-5 max-w-lg mx-auto">
      <div>
        <h2 className="font-heading font-bold text-xl text-stone-900">Über dich & deine Marke</h2>
        <p className="text-sm text-stone-500 mt-1">
          Damit deine Posts nach <em>dir</em> klingen — nicht nach Vorlage.
        </p>
      </div>

      {/* Unternehmen */}
      <div className="space-y-1.5">
        <label className="text-xs font-semibold text-stone-500 uppercase tracking-wide">
          Wie heißt du oder dein Angebot?
        </label>
        <input
          type="text"
          value={brand.company_name}
          onChange={e => update("company_name", e.target.value)}
          placeholder="z. B. Elektro Müller GmbH"
          className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-fox-fire focus:ring-1 focus:ring-fox-fire outline-none"
        />
      </div>

      {/* Angebot */}
      <div className="space-y-1.5">
        <label className="text-xs font-semibold text-stone-500 uppercase tracking-wide">
          Was bietest du an?
        </label>
        <input
          type="text"
          value={brand.offer}
          onChange={e => update("offer", e.target.value)}
          placeholder="z.B. Bewerbungscoaching für Arbeitssuchende"
          className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-fox-fire focus:ring-1 focus:ring-fox-fire outline-none"
        />
      </div>

      {/* Zielgruppe */}
      <div className="space-y-1.5">
        <label className="text-xs font-semibold text-stone-500 uppercase tracking-wide">
          Für wen? (deine Zielgruppe)
        </label>
        <input
          type="text"
          value={brand.audience}
          onChange={e => update("audience", e.target.value)}
          placeholder="z.B. Berufseinsteiger & Menschen in Veränderung"
          className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-fox-fire focus:ring-1 focus:ring-fox-fire outline-none"
        />
      </div>

      {/* Themen */}
      <div className="space-y-2">
        <label className="text-xs font-semibold text-stone-500 uppercase tracking-wide">
          Worüber soll gepostet werden?
        </label>
        {/* gewählte Themen */}
        {brand.topics.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {brand.topics.map(t => (
              <span key={t} className="flex items-center gap-1 text-xs px-2.5 py-1 bg-orange-100 text-orange-700 rounded-full">
                {t}
                <button onClick={() => removeTopic(t)} className="hover:text-orange-900">
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        )}
        {/* Eingabe */}
        <div className="flex gap-2">
          <input
            type="text"
            value={topicInput}
            onChange={e => setTopicInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addTopic(); } }}
            placeholder="Thema eingeben und Enter"
            className="flex-1 px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-fox-fire focus:ring-1 focus:ring-fox-fire outline-none"
          />
          <button
            onClick={() => addTopic()}
            className="px-3 py-2 rounded-xl bg-stone-100 text-stone-600 hover:bg-stone-200 transition-colors"
            title="Thema hinzufügen"
          >
            <Plus size={16} />
          </button>
        </div>
        {/* Vorschläge */}
        <div className="flex flex-wrap gap-1.5 pt-1">
          {TOPIC_SUGGESTIONS.filter(s => !brand.topics.includes(s)).map(s => (
            <button
              key={s}
              onClick={() => addTopic(s)}
              className="text-xs px-2.5 py-1 rounded-full border border-dashed border-stone-300 text-stone-500 hover:border-fox-fire hover:text-fox-fire transition-colors"
            >
              + {s}
            </button>
          ))}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center gap-3 pt-2">
        <button onClick={onBack} className="flex items-center gap-1 px-4 py-2.5 text-sm text-stone-500 hover:text-stone-700 rounded-xl hover:bg-stone-100 transition-colors">
          <ChevronLeft size={16} /> Zurück
        </button>
        <button
          onClick={onNext}
          disabled={!canProceed}
          className="flex-1 flex items-center justify-center gap-2 px-6 py-2.5 bg-fox-fire text-white rounded-xl font-semibold text-sm hover:bg-orange-600 disabled:opacity-40 transition-colors"
        >
          Weiter <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}

// ── Schritt 3: Ziele, Tonalität & Kanäle ──────────────────────────────────────
function StepSettings({ goals, setGoals, intensity, setIntensity, platforms, setPlatforms, tone, setTone, onNext, onBack }) {
  const toggleGoal = (id) => setGoals(prev =>
    prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]
  );
  const togglePlatform = (id) => setPlatforms(prev =>
    prev.includes(id)
      ? prev.length > 1 ? prev.filter(p => p !== id) : prev  // min 1
      : [...prev, id]
  );

  const canProceed = goals.length > 0 && platforms.length > 0;

  return (
    <div className="px-6 py-6 space-y-6 max-w-lg mx-auto">
      <div>
        <h2 className="font-heading font-bold text-xl text-stone-900">Ziele, Stimme & Kanäle</h2>
        <p className="text-sm text-stone-500 mt-1">So passt Förderfuchs deinen Plan an dich an.</p>
      </div>

      {/* Tonalität */}
      <div className="space-y-2">
        <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide">
          Welche Tonalität passt zu dir?
        </p>
        <div className="flex flex-wrap gap-2">
          {TONES.map(t => (
            <button
              key={t.id}
              onClick={() => setTone(t.id)}
              className={`px-3 py-2 rounded-xl text-sm font-medium border transition-all ${
                tone === t.id
                  ? "bg-violet-600 text-white border-violet-600 shadow-sm"
                  : "bg-white text-stone-600 border-stone-200 hover:border-stone-300"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Ziele */}
      <div className="space-y-2">
        <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide">
          Was willst du erreichen? (mehrere möglich)
        </p>
        <div className="flex flex-wrap gap-2">
          {GOALS.map(g => (
            <button
              key={g.id}
              onClick={() => toggleGoal(g.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium border transition-all ${
                goals.includes(g.id)
                  ? "bg-fox-fire text-white border-fox-fire shadow-sm"
                  : "bg-white text-stone-600 border-stone-200 hover:border-stone-300"
              }`}
            >
              <span>{g.emoji}</span>
              {g.label}
            </button>
          ))}
        </div>
      </div>

      {/* Intensität */}
      <div className="space-y-2">
        <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide">
          Wie aktiv willst du posten?
        </p>
        <div className="grid grid-cols-3 gap-2">
          {INTENSITIES.map(i => (
            <button
              key={i.id}
              onClick={() => setIntensity(i.id)}
              className={`flex flex-col items-center gap-1 px-2 py-3 rounded-xl border text-center transition-all ${
                intensity === i.id
                  ? "bg-fox-fire/10 border-fox-fire text-fox-fire ring-1 ring-fox-fire"
                  : "bg-white border-stone-200 text-stone-600 hover:border-stone-300"
              }`}
            >
              <span className="text-xl">{i.emoji}</span>
              <span className="text-sm font-semibold">{i.label}</span>
              <span className="text-[10px] text-stone-400">{i.sub}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Plattformen */}
      <div className="space-y-2">
        <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide">
          Für welche Kanäle soll ich Posts erstellen?
        </p>
        <div className="flex flex-wrap gap-2">
          {PLATFORMS.map(p => (
            <button
              key={p.id}
              onClick={() => togglePlatform(p.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium border transition-all ${
                platforms.includes(p.id)
                  ? "bg-stone-800 text-white border-stone-800"
                  : "bg-white text-stone-600 border-stone-200 hover:border-stone-300"
              }`}
            >
              <span>{p.emoji}</span>
              {p.label}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-stone-400">
          Noch nicht verbunden? Prima — plan jetzt, verbinde die Kanäle in deinem eigenen Tempo.
        </p>
      </div>

      {/* Navigation */}
      <div className="flex items-center gap-3 pt-2">
        <button onClick={onBack} className="flex items-center gap-1 px-4 py-2.5 text-sm text-stone-500 hover:text-stone-700 rounded-xl hover:bg-stone-100 transition-colors">
          <ChevronLeft size={16} /> Zurück
        </button>
        <button
          onClick={onNext}
          disabled={!canProceed}
          className="flex-1 flex items-center justify-center gap-2 px-6 py-2.5 bg-fox-fire text-white rounded-xl font-semibold text-sm hover:bg-orange-600 disabled:opacity-40 transition-colors"
        >
          Weiter <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}

// ── Schritt 4: Plan generieren ────────────────────────────────────────────────
function StepGenerate({ brand, goals, intensity, platforms, tone, onSaveProfile, onComplete, onSkipToCalendar, onBack }) {
  const navigate   = useNavigate();
  const [phase, setPhase]   = useState("idle");   // idle | loading | success | error
  const [result, setResult] = useState(null);
  const [errMsg, setErrMsg] = useState(null);
  // Kanal-Verbindungsstatus für den Ausstiegs-Hinweis (STRATEGIE_WETTBEWERB.md:
  // größter Früh-Churn-Hebel) — null = noch unbekannt/Abfrage läuft, fail-open.
  const [channelsConnected, setChannelsConnected] = useState(null);
  const [confirmNoChannel, setConfirmNoChannel]   = useState(false);

  const handleGenerate = useCallback(async () => {
    setPhase("loading");
    setErrMsg(null);
    try {
      // 1 — Markenprofil + Plan-Defaults speichern (defensiv; blockiert den Plan nicht)
      await onSaveProfile?.();

      // 2 — Plan generieren (Themen fließen als Schwerpunkte ein)
      const res = await axios.post(`${API}/autopilot/generate-monthly-plan`, {
        month:        CURRENT_MONTH,
        year:         CURRENT_YEAR,
        goals:        goals.length > 0 ? goals : ["Sichtbarkeit"],
        intensity:    intensity || "normal",
        schwerpunkte: (brand.topics || []).slice(0, 6),
        platforms:    platforms.length > 0 ? platforms : ["bluesky", "instagram", "facebook"],
      });
      setResult(res.data);
      setPhase("success");
      trackEvent("monthly_plan_generated", {
        posts_count: res.data?.imported_count ?? res.data?.total_posts ?? 0,
        source: "onboarding_wizard",
        platforms: platforms,
      });

      // 3 — Onboarding abschließen (defensiv)
      try {
        await axios.post(`${API}/account/onboarding/complete`);
        localStorage.setItem("ff_onboarding_done", "1");
      } catch (_) { /* unkritisch */ }

    } catch (err) {
      // Detail kann bei 422 ein Objekt/Array sein — nie roh rendern (sonst „[object Object]").
      const d = err.response?.data?.detail;
      setErrMsg(typeof d === "string" ? d : "Der Plan konnte nicht erstellt werden. Bitte versuche es erneut.");
      setPhase("error");
    }
  }, [brand, goals, intensity, platforms, onSaveProfile]);

  // Sobald der Plan steht, best-effort prüfen, ob überhaupt schon ein Kanal
  // verbunden ist — entscheidet, ob "Meinen Plan ansehen" zuerst einen Hinweis
  // zeigen muss (siehe onboardingCompletionModel.js). Schlägt die Abfrage fehl,
  // bleibt channelsConnected null → fail-open, kein Hinweis.
  useEffect(() => {
    if (phase !== "success") return;
    let cancelled = false;
    axios.get(`${API}/simple-flow/channels`)
      .then(res => { if (!cancelled) setChannelsConnected(!!res.data?.has_connected_channels); })
      .catch(() => { if (!cancelled) setChannelsConnected(null); });
    return () => { cancelled = true; };
  }, [phase]);

  const goToCalendarNow = () => {
    onComplete?.();
    navigate("/monthly-plan", { state: { fromOnboarding: true } });
  };

  const handleGoToCalendar = () => {
    // Strukturelle Lücke (STRATEGIE_WETTBEWERB.md, TODO.md 27.08.2026): dieser
    // Knopf verließ den Wizard bisher IMMER sofort in den Kalender — auch wenn
    // noch kein Kanal verbunden war. Der Wizard gilt danach als abgeschlossen
    // (ff_onboarding_done) und kommt nie zurück. Ist bekannt, dass kein Kanal
    // verbunden ist, zeigen wir erst eine Erklärung statt still zu verlassen;
    // ein zweiter, bestätigter Klick lässt den Nutzer trotzdem weiter (die App
    // sperrt niemanden ein, siehe App.js-Onboarding-Check).
    if (shouldConfirmChannellessExit(channelsConnected, confirmNoChannel)) {
      setConfirmNoChannel(true);
      return;
    }
    goToCalendarNow();
  };

  // Letzter Schritt der Reise: Kanal verbinden → ersten Beitrag live stellen.
  const handleConnectAndGoLive = () => {
    onComplete?.();
    navigate("/kanale", { state: { fromOnboarding: true } });
  };

  return (
    <div className="px-6 py-6 space-y-5 max-w-lg mx-auto">
      <div>
        <h2 className="font-heading font-bold text-xl text-stone-900">Deinen ersten Monatsplan erstellen</h2>
        <p className="text-sm text-stone-500 mt-1">
          {MONTH_NAMES[CURRENT_MONTH]} {CURRENT_YEAR} — {
            intensity === "leicht" ? "entspannte Frequenz" :
            intensity === "intensiv" ? "aktive Frequenz" : "regelmäßige Frequenz"
          }
        </p>
      </div>

      {/* Zusammenfassung der Einstellungen */}
      {phase === "idle" && (
        <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 space-y-2">
          <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">Deine Angaben</p>
          {brand.company_name && (
            <p className="text-sm text-stone-700"><span className="text-stone-400">Marke:</span> {brand.company_name}</p>
          )}
          {brand.offer && (
            <p className="text-sm text-stone-700"><span className="text-stone-400">Angebot:</span> {brand.offer}</p>
          )}
          <div className="flex flex-wrap gap-1.5 pt-1">
            {(brand.topics || []).map(t => (
              <span key={t} className="text-[11px] px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full">{t}</span>
            ))}
            {goals.map(g => (
              <span key={g} className="text-[11px] px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">{g}</span>
            ))}
            {platforms.map(p => (
              <span key={p} className="text-[11px] px-2 py-0.5 bg-sky-100 text-sky-700 rounded-full">{p}</span>
            ))}
            <span className="text-[11px] px-2 py-0.5 bg-stone-200 text-stone-600 rounded-full">
              {intensity === "leicht" ? "🌿 Entspannt" : intensity === "intensiv" ? "⚡ Aktiv" : "🔥 Regelmäßig"}
            </span>
          </div>
        </div>
      )}

      {/* Loading */}
      {phase === "loading" && (
        <div className="flex flex-col items-center gap-4 py-12 text-center">
          <div className="w-16 h-16 bg-orange-50 rounded-2xl flex items-center justify-center">
            <Loader2 size={32} className="text-fox-fire animate-spin" />
          </div>
          <div>
            <p className="font-semibold text-stone-800">Plane deinen Monat…</p>
            <p className="text-sm text-stone-400 mt-1">Die KI verteilt Posts, Themen und Formate automatisch.</p>
          </div>
        </div>
      )}

      {/* Error */}
      {phase === "error" && (
        <div className="space-y-4">
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center">
              <AlertTriangle size={28} className="text-red-400" />
            </div>
            <div>
              <p className="font-semibold text-stone-800">Plan konnte nicht erstellt werden</p>
              <p className="text-sm text-stone-500 mt-1 max-w-xs mx-auto">{errMsg}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleGenerate}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-fox-fire text-white rounded-xl text-sm font-semibold hover:bg-orange-600 transition-colors"
            >
              <Sparkles size={14} /> Erneut versuchen
            </button>
            <button
              onClick={onSkipToCalendar}
              className="px-4 py-2.5 text-sm text-stone-500 hover:text-stone-700 rounded-xl hover:bg-stone-100 transition-colors"
            >
              Trotzdem zum Kalender
            </button>
          </div>
        </div>
      )}

      {/* Success */}
      {phase === "success" && result && (
        <div className="space-y-5">
          <div className="flex flex-col items-center gap-3 text-center">
            <div className="w-16 h-16 bg-green-50 rounded-2xl flex items-center justify-center">
              <CheckCircle size={32} className="text-green-500" />
            </div>
            <div>
              <p className="font-heading font-bold text-xl text-stone-900">
                {result.imported_count ?? result.total_posts} Beiträge als Entwurf erstellt!
              </p>
              <p className="text-sm text-stone-500 mt-1">
                {MONTH_NAMES[CURRENT_MONTH]} {CURRENT_YEAR}
                {result.bonus_posts_added > 0 && (
                  <span className="ml-2 text-violet-600">+{result.bonus_posts_added} Bonus-Posts 🦊</span>
                )}
              </p>
            </div>
          </div>

          {/* Nur noch ein Schritt bis zum ersten Live-Post */}
          <div className="bg-orange-50 border border-orange-100 rounded-xl p-4">
            <p className="text-sm font-semibold text-stone-800 mb-2.5">Nur noch bis live:</p>
            <ol className="space-y-2">
              {[
                "Verbinde einen Kanal — in der Testphase startest du mit Bluesky",
                "Gib deinen ersten Beitrag frei & veröffentliche ihn",
              ].map((t, i) => (
                <li key={i} className="flex items-center gap-2.5 text-sm text-stone-700">
                  <span className="w-5 h-5 rounded-full bg-fox-fire text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                    {i + 1}
                  </span>
                  {t}
                </li>
              ))}
            </ol>
          </div>

          {confirmNoChannel ? (
            // Bestätigter Ausstieg ohne Kanal (onboardingCompletionModel.js): statt
            // still zu verlassen, erst die Konsequenz klarmachen — zweiter Klick
            // entscheidet sich bewusst dagegen oder dafür.
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3" data-testid="onboarding-no-channel-warning">
              <div className="flex items-start gap-2.5">
                <AlertTriangle size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-stone-700">
                  Noch kein Kanal verbunden — ohne Kanal bleibt dein Plan nur ein Entwurf
                  und wird nie veröffentlicht.
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleConnectAndGoLive}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-fox-fire text-white rounded-xl text-sm font-bold hover:bg-orange-600 transition-colors"
                >
                  <Wifi size={14} /> Jetzt Kanal verbinden
                </button>
                <button
                  onClick={goToCalendarNow}
                  className="px-4 py-2.5 text-sm text-stone-500 hover:text-stone-700 rounded-xl hover:bg-stone-100 transition-colors"
                >
                  Trotzdem ohne Kanal
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Fund 25.08.2026: dieser Knopf war vorher der kleine, graue Sekundär-
                  Knopf — der optisch dominante Weg (fett, orange) führte direkt in den
                  leeren Kalender, OHNE Kanalverbindung, und widersprach damit der
                  eigenen Anleitung direkt darüber ("1. Verbinde einen Kanal..."). Jetzt
                  ist der Weg, der wirklich zum ersten Live-Post führt, auch optisch der
                  empfohlene — der größte Früh-Churn-Hebel laut
                  docs/STRATEGIE_WETTBEWERB.md. */}
              <button
                onClick={handleConnectAndGoLive}
                className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-fox-fire text-white rounded-xl font-bold text-base hover:bg-orange-600 transition-colors shadow-sm shadow-orange-200"
              >
                <Wifi size={16} /> Kanal verbinden & ersten Post live stellen
              </button>
              <button
                onClick={handleGoToCalendar}
                className="w-full flex items-center justify-center gap-2 px-6 py-2.5 text-stone-500 hover:text-stone-700 rounded-xl hover:bg-stone-100 transition-colors text-sm font-medium"
              >
                Meinen Plan ansehen <ArrowRight size={18} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Idle CTA */}
      {phase === "idle" && (
        <div className="flex items-center gap-3 pt-1">
          <button onClick={onBack} className="flex items-center gap-1 px-4 py-2.5 text-sm text-stone-500 hover:text-stone-700 rounded-xl hover:bg-stone-100 transition-colors">
            <ChevronLeft size={16} /> Zurück
          </button>
          <button
            onClick={handleGenerate}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-fox-fire text-white rounded-xl font-bold text-base hover:bg-orange-600 transition-colors shadow-sm shadow-orange-200"
          >
            <Sparkles size={18} />
            Monatsplan mit 1 Klick erzeugen
          </button>
        </div>
      )}
    </div>
  );
}

// ── Hauptkomponente ────────────────────────────────────────────────────────────
export default function OnboardingWizard({ onComplete, onSkip }) {
  const navigate = useNavigate();

  const [step, setStep]             = useState(0);
  // Step "Marke"
  const [brand, setBrand]           = useState({
    company_name: "", offer: "", audience: "", topics: [],
  });
  // Step "Einstellungen"
  const [tone, setTone]             = useState("warm");
  const [goals, setGoals]           = useState(["Sichtbarkeit"]);
  const [intensity, setIntensity]   = useState("normal");
  const [platforms, setPlatforms]   = useState(["bluesky", "instagram", "facebook"]);

  // AB-01: Variante einmalig beim Laden zuweisen und persistieren
  const ab01Variant = useRef("A");
  useEffect(() => {
    ab01Variant.current = getVariant("AB-01", { trackFn: trackEvent });
  }, []);

  // T-02: Step-Views tracken
  useEffect(() => {
    trackEvent("onboarding_step_viewed", { step });
  }, [step]);

  // Markenprofil + Plan-Defaults im Account speichern (defensiv — nie blockierend)
  const saveProfile = useCallback(async () => {
    try {
      await axios.patch(`${API}/account/brand-profile`, {
        company_name: brand.company_name,
        offer:        brand.offer,
        audience:     brand.audience,
        topics:       brand.topics,
        tone,
        goals,
        intensity,
        platforms,
      });
    } catch (_) { /* unkritisch — Plan wird trotzdem erzeugt */ }
  }, [brand, tone, goals, intensity, platforms]);

  const handleSkip = async () => {
    // Profil best-effort speichern, Onboarding als erledigt markieren
    trackEvent("onboarding_abandoned", { step, reason: "skip" });
    await saveProfile();
    try {
      await axios.post(`${API}/account/onboarding/complete`);
      localStorage.setItem("ff_onboarding_done", "1");
    } catch (_) { /* unkritisch */ }
    onSkip?.();
    navigate("/monthly-plan");
  };

  const TOTAL_STEPS = 4;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      style={{ animation: "fadeIn 0.25s ease" }}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[92vh] flex flex-col overflow-hidden"
        style={{ animation: "slideUp 0.3s ease" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-0 flex-shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-lg">🦊</span>
            <span className="text-sm font-semibold text-stone-500">Förderfuchs — Erste Schritte</span>
          </div>
          {step > 0 && (
            <button
              onClick={handleSkip}
              className="text-stone-400 hover:text-stone-600 transition-colors"
              title="Wizard schließen"
            >
              <X size={18} />
            </button>
          )}
        </div>

        {/* Step Progress Bar */}
        <StepBar step={step} total={TOTAL_STEPS} />

        {/* Step Content (scrollable) */}
        <div className="flex-1 overflow-y-auto">
          {step === 0 && (
            <StepWelcome
              onNext={() => {
                trackEvent("onboarding_step_cta_clicked", { step: 0, variant: ab01Variant.current });
                setStep(1);
              }}
              onSkip={handleSkip}
              ctaLabel={
                ab01Variant.current === "B"
                  ? "Meinen ersten Monatsplan erstellen"
                  : "Jetzt in 2 Minuten einrichten"
              }
            />
          )}
          {step === 1 && (
            <StepBrand
              brand={brand} setBrand={setBrand}
              onNext={() => setStep(2)}
              onBack={() => setStep(0)}
            />
          )}
          {step === 2 && (
            <StepSettings
              goals={goals}         setGoals={setGoals}
              intensity={intensity} setIntensity={setIntensity}
              platforms={platforms} setPlatforms={setPlatforms}
              tone={tone}           setTone={setTone}
              onNext={() => setStep(3)}
              onBack={() => setStep(1)}
            />
          )}
          {step === 3 && (
            <StepGenerate
              brand={brand}
              goals={goals}
              intensity={intensity}
              platforms={platforms}
              tone={tone}
              onSaveProfile={saveProfile}
              onComplete={onComplete}
              onSkipToCalendar={handleSkip}
              onBack={() => setStep(2)}
            />
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn  { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}
